/**
 * PDF Export API Client
 * Handles PDF report generation and download
 */

import { API_BASE_URL } from './config'
import { logger } from '@/utils/logger'
import { showToast } from '@/components/Notifications/Toast'

export interface PDFExportOptions {
  title?: string
  includeCharts?: boolean
  includeDetails?: boolean
  dateRange?: { start: Date; end: Date }
  componentIds?: string[]
  riskLevelFilter?: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW'
}

export type ReportType = 'fmea' | 'summary' | 'pareto' | 'component'

/**
 * Download a PDF file from blob data
 * Creates a temporary <a> element and triggers download
 */
function downloadPDF(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

/**
 * Generate and download full FMEA report
 */
export async function exportFMEAReport(options: PDFExportOptions = {}): Promise<void> {
  const loadingToast = showToast.loading('Generating PDF report...')

  try {
    const response = await fetch(`${API_BASE_URL}/reports/pdf/fmea`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(options),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }))
      throw new Error(error.message || `HTTP Error: ${response.status}`)
    }

    // Get filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition')
    const filenameMatch = contentDisposition?.match(/filename="?(.+)"?/)
    const filename = filenameMatch?.[1] || `FMEA_Report_${Date.now()}.pdf`

    const blob = await response.blob()
    downloadPDF(blob, filename)

    showToast.success('PDF report downloaded successfully', { id: loadingToast })
  } catch (error) {
    logger.error('PDF export error', error, { module: 'pdf-export' });
    showToast.error(error instanceof Error ? error.message : 'Failed to generate PDF report', {
      id: loadingToast,
    })
    throw error
  }
}

/**
 * Generate and download risk summary report (2-3 pages)
 */
export async function exportRiskSummaryReport(): Promise<void> {
  const loadingToast = showToast.loading('Generating risk summary...')

  try {
    const response = await fetch(`${API_BASE_URL}/reports/pdf/risk-summary`, {
      method: 'GET',
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }))
      throw new Error(error.message || `HTTP Error: ${response.status}`)
    }

    const contentDisposition = response.headers.get('Content-Disposition')
    const filenameMatch = contentDisposition?.match(/filename="?(.+)"?/)
    const filename = filenameMatch?.[1] || `Risk_Summary_${Date.now()}.pdf`

    const blob = await response.blob()
    downloadPDF(blob, filename)

    showToast.success('Risk summary downloaded successfully', { id: loadingToast })
  } catch (error) {
    logger.error('Risk summary export error', error, { module: 'pdf-export' });
    showToast.error(error instanceof Error ? error.message : 'Failed to generate risk summary', {
      id: loadingToast,
    })
    throw error
  }
}

/**
 * Generate and download Pareto analysis report
 */
export async function exportParetoReport(): Promise<void> {
  const loadingToast = showToast.loading('Generating Pareto analysis...')

  try {
    const response = await fetch(`${API_BASE_URL}/reports/pdf/pareto`, {
      method: 'GET',
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }))
      throw new Error(error.message || `HTTP Error: ${response.status}`)
    }

    const contentDisposition = response.headers.get('Content-Disposition')
    const filenameMatch = contentDisposition?.match(/filename="?(.+)"?/)
    const filename = filenameMatch?.[1] || `Pareto_Analysis_${Date.now()}.pdf`

    const blob = await response.blob()
    downloadPDF(blob, filename)

    showToast.success('Pareto report downloaded successfully', { id: loadingToast })
  } catch (error) {
    logger.error('Pareto export error', error, { module: 'pdf-export' });
    showToast.error(error instanceof Error ? error.message : 'Failed to generate Pareto report', {
      id: loadingToast,
    })
    throw error
  }
}

/**
 * Generate and download component-specific report
 */
export async function exportComponentReport(componentId: string): Promise<void> {
  const loadingToast = showToast.loading('Generating component report...')

  try {
    const response = await fetch(`${API_BASE_URL}/reports/pdf/component/${componentId}`, {
      method: 'GET',
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }))
      throw new Error(error.message || `HTTP Error: ${response.status}`)
    }

    const contentDisposition = response.headers.get('Content-Disposition')
    const filenameMatch = contentDisposition?.match(/filename="?(.+)"?/)
    const filename = filenameMatch?.[1] || `Component_Report_${Date.now()}.pdf`

    const blob = await response.blob()
    downloadPDF(blob, filename)

    showToast.success('Component report downloaded successfully', { id: loadingToast })
  } catch (error) {
    logger.error('Component report export error', error, { module: 'pdf-export' });
    showToast.error(error instanceof Error ? error.message : 'Failed to generate component report', {
      id: loadingToast,
    })
    throw error
  }
}

/**
 * Generate and download custom report
 */
export async function exportCustomReport(
  reportType: ReportType,
  options?: PDFExportOptions,
  componentId?: string
): Promise<void> {
  const loadingToast = showToast.loading('Generating custom report...')

  try {
    const response = await fetch(`${API_BASE_URL}/reports/pdf/custom`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        reportType,
        componentId,
        options,
      }),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }))
      throw new Error(error.message || `HTTP Error: ${response.status}`)
    }

    const contentDisposition = response.headers.get('Content-Disposition')
    const filenameMatch = contentDisposition?.match(/filename="?(.+)"?/)
    const filename = filenameMatch?.[1] || `Custom_Report_${Date.now()}.pdf`

    const blob = await response.blob()
    downloadPDF(blob, filename)

    showToast.success('Custom report downloaded successfully', { id: loadingToast })
  } catch (error) {
    logger.error('Custom report export error', error, { module: 'pdf-export' });
    showToast.error(error instanceof Error ? error.message : 'Failed to generate custom report', {
      id: loadingToast,
    })
    throw error
  }
}
